import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY, LOCALES, type Locale } from './types';
import { en } from './locales/en';
import { es } from './locales/es';
import { guideEn } from './locales/guide.en';
import { guideEs } from './locales/guide.es';
import type { LandingMessages } from './types';
import type { ApiKeysGuideMessages } from './guideTypes';

const messages: Record<Locale, LandingMessages> = { en, es };
const guideMessages: Record<Locale, ApiKeysGuideMessages> = { en: guideEn, es: guideEs };

export function isLocale(value: string | null | undefined): value is Locale {
  return LOCALES.includes(value as Locale);
}

/** Prefer saved choice; otherwise map browser language (es* → Spanish, else English). */
export function detectLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;

  try {
    const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(saved)) return saved;
  } catch {
    // ignore storage errors
  }

  const candidates = [
    ...(navigator.languages ?? []),
    navigator.language,
  ].filter(Boolean);

  for (const tag of candidates) {
    const base = tag.toLowerCase().split('-')[0];
    if (base === 'es') return 'es';
    if (base === 'en') return 'en';
  }

  return DEFAULT_LOCALE;
}

export function persistLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore storage errors
  }
}

export function getMessages(locale: Locale): LandingMessages {
  return messages[locale] ?? messages[DEFAULT_LOCALE];
}

export function getApiKeysGuideMessages(locale: Locale): ApiKeysGuideMessages {
  return guideMessages[locale] ?? guideMessages[DEFAULT_LOCALE];
}

export function formatMessage(template: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce(
    (text, [key, value]) => text.replace(new RegExp(`\\{${key}\\}`, 'g'), String(value)),
    template
  );
}
